// src/config/roles/superuserConfig.js
//
// Client-superuser routes. Two layers:
//   1. Portfolio pages (client-scoped) — shown in the sidebar.
//   2. A spread of the manager's property-scoped routes, forced showInNav:false,
//      so that drilling into a single property (setSelectedProperty(id, clientId)
//      + navigate("/rooms")) resolves the existing manager views without
//      rebuilding them. Portfolio paths take precedence and are excluded from
//      the spread so there are no duplicate <Route path> collisions.
//
// See SUPERUSER_ROUTES_DESIGN.md.

import PublicIcon from "@mui/icons-material/Public";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PeopleIcon from "@mui/icons-material/People";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InboxIcon from "@mui/icons-material/Inbox";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { managerConfig } from "./managerConfig";
import PortfolioDashboard from "../../components/superuser/PortfolioDashboard";
import PortfolioProperties from "../../components/superuser/PortfolioProperties";
import PortfolioUsers from "../../components/superuser/PortfolioUsers";
import Billing from "../../components/superuser/Billing";
import ClientSettings from "../../components/superuser/ClientSettings";
import { makePlaceholder } from "../../components/superuser/PortfolioPlaceholder";

// ── Portfolio pages (sidebar) ────────────────────────────────────────────────
const portfolioRoutes = [
  {
    path: "/",
    component: PortfolioDashboard,
    label: "Dashboard",
    icon: PublicIcon,
    showInNav: true,
    default: true,
  },
  {
    path: "/properties",
    component: PortfolioProperties,
    label: "Properties",
    icon: ApartmentIcon,
    showInNav: true,
  },
  {
    path: "/users",
    component: PortfolioUsers,
    label: "Users",
    icon: PeopleIcon,
    showInNav: true,
  },
  {
    path: "/portfolio-reports",
    component: makePlaceholder(
      "Portfolio Reports",
      "Cross-property reporting: down-room aging, cost to make ready, PM compliance, spend by vendor, and portfolio comparisons. Coming soon."
    ),
    label: "Reports",
    icon: AssessmentIcon,
    showInNav: true,
  },
  {
    path: "/approvals",
    component: makePlaceholder(
      "Approvals",
      "Portfolio work queue: purchase and parts approvals, high-cost work orders, overdue PMs, and escalated guest complaints. Coming soon."
    ),
    label: "Approvals",
    icon: InboxIcon,
    showInNav: true,
    badge: true,
  },
  {
    path: "/audit-log",
    component: makePlaceholder(
      "Audit Log",
      "Who changed what, when, and where — across every property in the client. Coming soon."
    ),
    label: "Audit Log",
    icon: HistoryIcon,
    showInNav: true,
  },
  {
    path: "/client-settings",
    component: ClientSettings,
    label: "Settings",
    icon: SettingsIcon,
    showInNav: true,
  },
  {
    path: "/billing",
    component: Billing,
    label: "Billing",
    icon: ReceiptLongIcon,
    showInNav: true,
  },
];

// ── Drill-down: reuse manager property views, hidden from the portfolio nav ──
const portfolioPaths = new Set(portfolioRoutes.map((r) => r.path));
const drillDownRoutes = managerConfig
  .filter((r) => !portfolioPaths.has(r.path))
  .map((r) => ({ ...r, showInNav: false }));

export const superuserConfig = [...portfolioRoutes, ...drillDownRoutes];

export const superuserMetadata = {
  roleName: "Client Superuser",
  defaultRoute: "/",
};
